import React, { useEffect, useImperativeHandle, useState } from 'react';
import useModal from '@app/component/overlays/modal/ModalRef';
import Modal from '@app/component/overlays/modal/Modal';
import { useForm } from 'react-hook-form';
import FormButton from '@app/component/form/button/FormButton';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import FormSelect from '@app/component/form/input/select/FormSelect';
import _isEmpty from 'lodash/isEmpty';

import useToaster from '@app/component/overlays/toaster/ToasterHook';
import FormInputText from '@app/component/form/input/text/FormInputText';
import {
	useDisableCompanyHotelRoomMappingTypeConfigMutation,
	useSaveCompanyHotelRoomMappingTypeConfigMutation,
} from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import RoomMappingTypeConfigurationOutput
	from '@app/model/company_hotel_room_mappings_type_configuration/RoomMappingTypeConfigurationOutput';

type MappingTypeOption = {
	name: string
	value: HotelRoomMappingType
}

const EditHotelRoomTypeModal: React.FunctionComponent<{
	ref: any,
	configs: RoomMappingTypeConfigurationOutput[]
}> = React.forwardRef(({ configs }, ref: React.Ref<any>) => {
	const modal = useModal();
	const form = useForm<{
		name: string,
		type: HotelRoomMappingType
	}>({
		defaultValues: {
			name: '',
			type: undefined,
		},
	});
	const [mappingTypeOptions, setMappingTypeOptions] = useState<MappingTypeOption[]>([]);
	const toaster = useToaster();
	const [position, setPosition] = useState<string>();
	const { companyHotel } = useSelectedCompanyHotel();

	useImperativeHandle(ref, () => {
		return {
			open: () => {
				modal.open();
				form.reset({
					name: '',
					type: undefined,
				});
			},
		};
	});

	useEffect(() => {
		if (!_isEmpty(configs)) {
			const options: MappingTypeOption[] = [];
			for (const c of configs) {
				if (c.isEnabled && !c.isSystem) {
					options.push({
						name: c.name,
						value: c.type,
					});
				}
			}
			setMappingTypeOptions(options);
		}
	}, [configs]);

	const typeWatch = form.watch('type');

	useEffect(() => {
		if (!!typeWatch && !_isEmpty(mappingTypeOptions)) {
			const option = mappingTypeOptions.find(o => o.value === typeWatch);
			if (!!option) {
				form.setValue('name', option.name);
				if (option.value.startsWith('SLOT_SINGLE_STANDARD_')) setPosition('After Single');
				if (option.value.startsWith('SLOT_STANDARD_PREMIUM_')) setPosition('After Standard');
				if (option.value.startsWith('SLOT_PREMIUM_SUITE_')) setPosition('After Premium');
				if (option.value.startsWith('SLOT_SUITE_NONE_')) setPosition('After Suite');
			}
		}
	}, [typeWatch]);

	const saveCompanyHotelRoomMappingTypeMutation = useSaveCompanyHotelRoomMappingTypeConfigMutation({
		onSuccess: (data) => {
			modal.close();
			toaster.success('Edit Room Type', 'Room type saved successfully!');
		},
	});

	const onSubmit = (data: { name: string, type: HotelRoomMappingType }) => {
		saveCompanyHotelRoomMappingTypeMutation.mutate({ companyHotelId: companyHotel.id, type: data.type, name: data.name });
	};

	const disableCompanyHotelRoomMappingTypeMutation = useDisableCompanyHotelRoomMappingTypeConfigMutation({
		onSuccess: (data) => {
			modal.close();
			toaster.success('Edit Room Type', 'Room type removed successfully!');
		},
	});

	const onRemove = () => {
		disableCompanyHotelRoomMappingTypeMutation.mutate({
			companyHotelId: companyHotel.id,
			type: form.getValues('type'),
		});
	};

	return <Modal ref={modal.ref} title="Edit Room Type" actions={
		!_isEmpty(mappingTypeOptions) &&
		<FormButton state="primary" onClick={form.handleSubmit(onSubmit)}>Submit</FormButton>
	}>
		<div className="space-y-4">
			<FormSelect name="type" label="Select Room Type" placeholder="Select room type" portal
									disabled={_isEmpty(mappingTypeOptions)} form={form}
									size="w-full"
									optionsSize="large"
									optionsTextSize="sm"
									options={mappingTypeOptions.map(o => {
										return { id: o.value, label: o.name, value: o.value };
									})} />
			{!!typeWatch && <div className="rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5 mt-6">
				<div className="border-b border-gray-900/5 px-4 py-4">
					<h3 className="text-sm font-semibold leading-6 text-gray-900">Edit Selected Room Type</h3>
				</div>
				<div className="px-4 py-4 space-y-4">
					<div>
						<FormInputText name="name" form={form} isRequired />
					</div>
					<div>
						<div className="flex justify-between">
							<label htmlFor="input-normal" className="label">Position</label>
						</div>
						<div className="mt-0.5">
							<input type="text" name="input-normal" id="input-normal"
										 defaultValue={position}
										 className="input input-disabled"
										 disabled={true}
										 placeholder="Insert room type name" />
						</div>
						<p className="help">The position cannot be changed. You should remove &amp; create it again.</p>
					</div>
				</div>
				<div className="border-t border-gray-900/5 px-4 py-4">
					<button type="button" className="btn btn-white text-red-500" onClick={onRemove}>Remove</button>
				</div>
			</div>}
		</div>

	</Modal>;

});

export default EditHotelRoomTypeModal;